const AVATAR_PALETTES = [
  ['#0A0A0A', '#1F2937', '#F5F5F4'],
  ['#111827', '#1D4ED8', '#EFF6FF'],
  ['#172554', '#0F766E', '#ECFEFF'],
  ['#1F2937', '#7C3AED', '#F5F3FF'],
  ['#3F3F46', '#EA580C', '#FFF7ED'],
  ['#0F172A', '#BE123C', '#FFF1F2'],
  ['#292524', '#047857', '#ECFDF5'],
  ['#1C1917', '#CA8A04', '#FEFCE8'],
] as const;

type AvatarOptions = {
  seed: string;
  size?: number;
};

function hashString(input: string) {
  let hash = 2166136261;

  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }

  return hash >>> 0;
}

function pickPalette(seed: string) {
  return AVATAR_PALETTES[hashString(seed) % AVATAR_PALETTES.length]!;
}

function clampSize(size?: number) {
  if (!size || Number.isNaN(size)) return 64;
  return Math.min(Math.max(Math.round(size), 32), 512);
}

export function buildAvatarSeed(...parts: Array<string | null | undefined>) {
  return parts
    .map((part) => part?.trim())
    .filter((part): part is string => Boolean(part))
    .join(':');
}

export function getAvatarUrl(
  seed: string,
  options?: {
    size?: number;
  },
) {
  const searchParams = new URLSearchParams();

  if (options?.size) {
    searchParams.set('size', String(options.size));
  }

  const query = searchParams.toString();
  return `/api/avatar/${encodeURIComponent(seed)}${query ? `?${query}` : ''}`;
}

export function getUserAvatarSrc(user: {
  uid?: string | null;
  nickname?: string | null;
  phone_number?: string | null;
  avatar_url?: string | null;
  head_img_url?: string | null;
}) {
  const imageSrc = user.avatar_url || user.head_img_url;

  if (imageSrc) {
    return imageSrc;
  }

  const seed = buildAvatarSeed(user.uid, user.phone_number, user.nickname) || 'guest';
  return getAvatarUrl(seed);
}

export function generateAvatarSvg({
  seed,
  size,
}: AvatarOptions) {
  const avatarSize = clampSize(size);
  const hash = hashString(seed);
  const [baseColor, accentColor] = pickPalette(seed);

  const gradientRotation = hash % 360;
  const circleX = 18 + (hash % 28);
  const circleY = 20 + ((hash >>> 5) % 24);
  const circleR = 18 + ((hash >>> 10) % 10);
  const rectRotation = -18 + ((hash >>> 15) % 36);
  const rectYOffset = 34 + ((hash >>> 20) % 12);
  const lineOpacity = 0.08 + ((hash >>> 24) % 8) * 0.02;
  const squareSize = 14 + ((hash >>> 12) % 12);
  const squareX = 32 + ((hash >>> 7) % 12);
  const squareY = 10 + ((hash >>> 18) % 12);
  const squareRotation = -12 + ((hash >>> 22) % 24);

  return `
<svg xmlns="http://www.w3.org/2000/svg" width="${avatarSize}" height="${avatarSize}" viewBox="0 0 64 64" fill="none" role="img" aria-label="Default avatar">
  <defs>
    <linearGradient id="bg" x1="0%" y1="0%" x2="100%" y2="100%" gradientTransform="rotate(${gradientRotation} 0.5 0.5)">
      <stop offset="0%" stop-color="${baseColor}" />
      <stop offset="100%" stop-color="${accentColor}" />
    </linearGradient>
    <clipPath id="clip">
      <rect width="64" height="64" rx="32" />
    </clipPath>
  </defs>
  <g clip-path="url(#clip)">
    <rect width="64" height="64" fill="url(#bg)" />
    <circle cx="${circleX}" cy="${circleY}" r="${circleR}" fill="white" opacity="0.08" />
    <rect x="-6" y="${rectYOffset}" width="76" height="20" rx="10" fill="white" opacity="0.12" transform="rotate(${rectRotation} 32 44)" />
    <rect x="${squareX}" y="${squareY}" width="${squareSize}" height="${squareSize}" rx="6" fill="white" opacity="0.18" transform="rotate(${squareRotation} ${squareX + squareSize / 2} ${squareY + squareSize / 2})" />
    <path d="M-4 17C9 9 21 13 31 18C41 23 52 27 68 21" stroke="white" stroke-opacity="${lineOpacity.toFixed(2)}" stroke-width="1.5" />
    <path d="M-2 49C10 40 22 42 32 46C44 51 53 55 68 49" stroke="white" stroke-opacity="${(lineOpacity + 0.05).toFixed(2)}" stroke-width="1.5" />
  </g>
</svg>`.trim();
}
